import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  NumPad,
  isMobile,
  RoundStatus,
  freeBetStore,
  useTranslation,
  gameConfigStore,
  BetResponseCode,
  applicationStore,
  notificationStore,
  useInputValueChangingFunctions,
} from '@pascal/pascal-core';
import { InputTypesForChanging } from '@pascal/pascal-core/shared/core/enums/additionalInputValueChangingButtons';

import {
  minOdd,
  defaultMinOdd,
  numPadSectionName,
  autoCashOutDataKeys,
} from '../../../enums/uiConfig';
import { BetSectionMain } from '../betSectionMain';
import { BetSectionHeader } from '../betSectionHeader';
import { doBetStore, gameStore } from '../../../store';
import NumpadWrapper from '../../mobile/numpadWrapper';
import { removeItemFromObject } from '../../../helpers';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { decidedCurrentValue } from '../../../helpers/supporters';
import { divide, toFixed } from '../../../helpers/arithmeticOperations';
import { FightersCashOutButtons } from '../../../components/fightersCashOutButtons';

let mobileWrRef;

export const BetSectionContent = observer(
  ({
    extraProps,
    handleChangeAutoBet,
    changeBetInputValue,
    handleSwitchAutoCashOut,
    handleChangeAutoCashOutInputValue,
  }) => {
    const {
      time,
      oddData,
      isRoundFin,
      isBetsClosed,
      currentJetsInfo,
      gameState: { status },
      numPadData: { sectionName, isOpen },
    } = gameStore;
    const { betData } = doBetStore;
    const t = useTranslation();
    const { limits } = gameConfigStore;
    const { isDemo } = applicationStore;

    const { activeFreeBet } = freeBetStore;

    const [isNotHaveActiveBets, setIsNotHaveActiveBets] = useState(null);
    const [isShowBettingPart, setIsShowBettingPart] = useState(false);

    const {
      amount,
      balance,
      isOpened,
      fighterData,
      autoCashOutOdd,
      handleInputBlur,
      currentAutoBetData,
      currentFighterSection,
      setActiveCurrentSection,
    } = extraProps;

    const { isAutoBet, autoBetCount } = currentAutoBetData;
    const { isBetAccepted, isDisabled, isNextRoundBet, freeBetId, freeBetMinOdd, fighterNumber } =
      fighterData;

    const { handleChangeInputValue } = useInputValueChangingFunctions({
      dividerOrMultiplayer: 2,
      changingValue: amount,
      changingValueCallback: doBetStore.setBetAmount,
      autoCashOutData: {
        isAutoCashOut: isOpened,
        minOdd: 1.01,
        minOddStep: 0.1,
        autoCashOutOdd,
        updateAutoCashOutValue: doBetStore.setAutoCashOutOdd,
      },
      autoCashOutValueChangingCallback: doBetStore.setAutoCashOutOdd,
      additionalInputInfo: { inputType: InputTypesForChanging.BetInput },
    });
    const { handleChangeInputValue: handleChangeAutoCashOutInput } = useInputValueChangingFunctions(
      {
        dividerOrMultiplayer: 2,
        changingValue: autoCashOutOdd,
        changingValueCallback: doBetStore.setAutoCashOutOdd,
        autoCashOutData: {
          isAutoCashOut: isOpened,
          minOdd: freeBetId ? freeBetMinOdd : 1.01,
          minOddStep: 0.1,
          autoCashOutOdd,
          updateAutoCashOutValue: doBetStore.setAutoCashOutOdd,
        },
        autoCashOutValueChangingCallback: doBetStore.setAutoCashOutOdd,
        additionalInputInfo: {
          inputType: InputTypesForChanging.AutoCashOutInput,
          maxValue: toFixed(divide(limits.maxWin, amount), 2),
          minValue: freeBetId ? freeBetMinOdd : 1.01,
        },
      }
    );
    const [namPudInputValue, updateNumPudInputValue] = useState(limits.minBet);

    const [ref, hasClickedOutside] = useClickOutside();

    useLayoutEffect(() => {
      mobileWrRef = document.querySelector('.mobile-wr');
    }, []);

    useEffect(() => {
      if (hasClickedOutside) {
        if (gameConfigStore?.limits.hasOwnProperty('step')) {
          gameConfigStore?.setLimits(removeItemFromObject(gameConfigStore?.limits, 'step'));
        }
        gameStore.setNumPadData({ isOpen: false, sectionName: null, currentInputValue: null });
      }
    }, [hasClickedOutside, currentFighterSection]);

    useEffect(() => {
      if (isDisabled && oddData) {
        doBetStore.updateIsDisabledBetSection(false, fighterNumber);
      }
    }, [isDisabled, oddData, fighterNumber]);

    const validatedNumPudInputValue = (callback) => {
      const { validatedBetAmount, errorMessageCode } = handleInputBlur(namPudInputValue);
      if (errorMessageCode) {
        notificationStore.setNotificationType({ responseCode: errorMessageCode });
      }
      callback(validatedBetAmount);
    };

    const changeAutoCashOutOdd = (betAmount) => {
      if (isOpened && autoCashOutOdd && autoCashOutOdd > divide(limits.maxWin, betAmount)) {
        doBetStore.updateAutoCashOutData(
          autoCashOutDataKeys.autoCashOutOdd,
          toFixed(divide(limits.maxWin, betAmount), 2),
          currentFighterSection
        );
        gameConfigStore.setMaxAutoCashOutOdd(toFixed(divide(limits.maxWin, betAmount), 2));
        notificationStore.setNotificationType({
          responseCode: BetResponseCode.InValidMaxAutoCashOutOdd,
        });
      }
    };

    const handleSubmit = (isConfirmed) => {
      const { manual, autoCashOutOdd } = numPadSectionName;
      const setNumPudValueBySectionName = {
        [manual]: () => {
          validatedNumPudInputValue(doBetStore.setBetAmount);
          const { validatedBetAmount } = handleInputBlur(namPudInputValue);
          if (isOpen) {
            changeAutoCashOutOdd(validatedBetAmount);
          }
        },
        [autoCashOutOdd]: () => {
          if (namPudInputValue > divide(limits.maxWin, amount)) {
            doBetStore.updateAutoCashOutData(
              autoCashOutDataKeys.isValidatedOdd,
              true,
              currentFighterSection
            );
            if (namPudInputValue > divide(limits.maxWin, amount)) {
              gameConfigStore.setMaxAutoCashOutOdd(toFixed(divide(limits.maxWin, amount), 2));
            }
            doBetStore.updateAutoCashOutData(
              autoCashOutDataKeys.autoCashOutOdd,
              decidedCurrentValue(
                namPudInputValue < divide(limits.maxWin, amount),
                toFixed(namPudInputValue, 2),
                toFixed(divide(limits.maxWin, amount), 2),
                notificationStore.setNotificationType,
                BetResponseCode.InValidMaxAutoCashOutOdd
              ),
              currentFighterSection
            );
            doBetStore.updateAutoCashOutData(
              autoCashOutDataKeys.isBlurred,
              true,
              currentFighterSection
            );
          } else if (
            !Number(namPudInputValue) ||
            Number(namPudInputValue) < (freeBetMinOdd || minOdd)
          ) {
            notificationStore.setNotificationType({
              responseCode: BetResponseCode.InValidAutoCashOutOdd,
            });
          } else {
            doBetStore.updateAutoCashOutData(
              autoCashOutDataKeys.autoCashOutOdd,
              namPudInputValue || minOdd,
              currentFighterSection
            );
          }
        },
      };
      if (isConfirmed) {
        setNumPudValueBySectionName?.[sectionName]();
        if (sectionName === autoCashOutOdd) {
          gameConfigStore?.setLimits(removeItemFromObject(gameConfigStore?.limits, 'step'));
        }
        gameStore.setNumPadData({ isOpen: false, sectionName: null, currentInputValue: null });
      }
    };

    const isDisabledFighterSection =
      isDisabled ||
      isBetAccepted ||
      (isAutoBet && autoBetCount && isBetAccepted) ||
      (!isBetAccepted && isNextRoundBet && isBetsClosed)
        ? 'disabled'
        : '';

    useEffect(() => {
      if ((activeFreeBet?.id && !freeBetId) || (freeBetId && !activeFreeBet?.id)) {
        const activeAutoCashOut = doBetStore.autoCashOutData.find((item) => item.isOpened);
        const activeAutoBet = doBetStore.autoBetData.find((item) => item.isAutoBet);
        const nextRoundBets = doBetStore.betData.filter((item) => item.isNextRoundBet);
        if ((activeAutoBet || activeAutoCashOut) && !nextRoundBets.length) {
          doBetStore.resetAutoBet();
          doBetStore.resetAutoCashuOutData();
        }

        if (nextRoundBets.length) {
          doBetStore.resetGameStates();
        }
      }
    }, [activeFreeBet?.id]);

    useEffect(() => {
      if (!isDemo) {
        if (activeFreeBet.id) {
          if (!isBetAccepted) {
            doBetStore.setBetAmount(activeFreeBet?.amount);
            doBetStore.setFreeBetData(
              activeFreeBet?.minOdd,
              activeFreeBet?.id,
              activeFreeBet?.maxPayout,
              activeFreeBet?.winType,
              currentFighterSection
            );
            doBetStore.updateAutoCashOutData(
              autoCashOutDataKeys.autoCashOutOdd,
              activeFreeBet?.id && [defaultMinOdd, minOdd].includes(autoCashOutOdd)
                ? activeFreeBet?.minOdd
                : autoCashOutOdd,
              currentFighterSection
            );
          }
        }

        if (!isBetAccepted && !activeFreeBet.id && freeBetId) {
          doBetStore.setBetAmount(limits?.defaultBetAmount || limits?.minBet);
          doBetStore.setFreeBetData(null, null, null, null, currentFighterSection);
        }
      }
    }, [isDemo, freeBetId, isBetAccepted, activeFreeBet?.id, currentFighterSection]);

    const handleOpenNumPad = (event, openedNumpadName) => {
      if (isMobile()) {
        updateNumPudInputValue(event.target.value);
        if (openedNumpadName === numPadSectionName.autoCashOutOdd) {
          gameConfigStore.setLimits({ ...gameConfigStore?.limits, step: minOdd });
        }
        gameStore.setNumPadData({
          isOpen: true,
          sectionName: openedNumpadName,
          currentInputValue: event.target.value,
        });
      }
    };

    const haveDoBet = useMemo(() => {
      return betData.find((el) => el.betId);
    }, [betData]);

    useEffect(() => {
      if (!isRoundFin && !isNotHaveActiveBets) {
        const winOrFinishedBetsData = betData.filter((el) => {
          if ((el.betId && (el.winAmount || el.finishedOdd)) || (!el.betId && oddData)) {
            return el;
          }
        });

        if (
          winOrFinishedBetsData.length === betData.length &&
          !isNotHaveActiveBets &&
          !isShowBettingPart
        ) {
          setTimeout(() => setIsNotHaveActiveBets(true), 1000);
          setIsShowBettingPart(true);
        }
      }

      if (isNotHaveActiveBets && time) {
        setIsNotHaveActiveBets(null);
        setIsShowBettingPart(false);
      }
    }, [betData, isRoundFin, isNotHaveActiveBets, oddData, time, isShowBettingPart]);

    return (
      <div className='parent-inner'>
        {/* ADD CLASS " cash-out " */}
        <div
          className={`bet-holder ${
            isMobile() && haveDoBet && oddData && !isNotHaveActiveBets ? 'cash-out' : ''
          }`}
        >
          <BetSectionHeader
            t={t}
            betData={betData}
            oddData={oddData}
            haveDoBet={haveDoBet}
            isRoundFin={isRoundFin}
            fighterData={fighterData}
            isBetsClosed={isBetsClosed}
            // isNotHaveActiveBets={isNotHaveActiveBets}
            currentFighterSection={currentFighterSection}
            setActiveCurrentSection={setActiveCurrentSection}
          />
          <BetSectionMain
            t={t}
            isDemo={isDemo}
            limits={limits}
            balance={balance}
            betAmount={amount}
            isAutoCashOut={isOpened}
            onBlur={handleInputBlur}
            fighterData={fighterData}
            isBetsClosed={isBetsClosed}
            activeFreeBet={activeFreeBet}
            isBetAccepted={isBetAccepted}
            autoCashOutOdd={autoCashOutOdd}
            autoBetData={currentAutoBetData}
            onChange={doBetStore.setBetAmount}
            handleOpenNumPad={handleOpenNumPad}
            doBetSender={doBetStore.doBetSender}
            changeBetAmount={changeBetInputValue}
            isStart={status === RoundStatus.Started}
            isNotHaveActiveBets={isNotHaveActiveBets}
            handleChangeAutoBet={handleChangeAutoBet}
            resetDoBetData={doBetStore.resetDoBetData}
            currentFighterSection={currentFighterSection}
            handleChangeInputValue={handleChangeInputValue}
            handleSwitchAutoCashOut={handleSwitchAutoCashOut}
            isDisabledFighterSection={isDisabledFighterSection}
            showNotification={notificationStore.setNotificationType}
            handleChangeAutoCashOutInput={handleChangeAutoCashOutInput}
            updateIsDisabledBetSection={doBetStore.updateIsDisabledBetSection}
            handleChangeAutoCashOutInputValue={handleChangeAutoCashOutInputValue}
          />
          <FightersCashOutButtons
            t={t}
            betData={betData}
            oddData={oddData}
            isRoundFin={isRoundFin}
            isBetsClosed={isBetsClosed}
            currentJetsInfo={currentJetsInfo}
            isNotHaveActiveBets={isNotHaveActiveBets}
            currentFighterSection={currentFighterSection}
            setActiveCurrentSection={setActiveCurrentSection}
          />
          <NumpadWrapper containerRef={mobileWrRef}>
            <div ref={ref} className='numpad-wr-for-outside-click'>
              <NumPad
                t={t}
                isOpen={isOpen}
                limits={limits}
                onSubmit={handleSubmit}
                amountValue={namPudInputValue}
                setAmountValue={updateNumPudInputValue}
              />
            </div>
          </NumpadWrapper>
        </div>
      </div>
    );
  }
);
